// src/features/admin/OverviewTab.tsx
// Platform health/growth at a glance, plus the wired shortcut to the
// compliance-grade audit trail explorer. GetAdminStats.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { adminStore } from '@core/stores';
import { Card, CardContent, ErrorAlert } from '@react/components';
import type { AdminStats } from '@core/types/admin';
import { bytesToHuman, minorToDisplay, errMessage } from './logic';

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export const OverviewTab = observer(() => {
  const { t } = useI18n();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminStore.GetAdminStats()
      .then((res) => setStats(res.stats ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('admin.overview.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) return <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  if (error) return <ErrorAlert error={error} />;
  if (!stats) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <StatCard label={t('admin.overview.tenants')} value={stats.active_tenants} sub={t('admin.overview.ofTotal', { count: stats.total_tenants })} />
        <StatCard label={t('admin.overview.users')} value={stats.active_users} sub={t('admin.overview.ofTotal', { count: stats.total_users })} />
        <StatCard label={t('admin.overview.newUsers24h')} value={stats.new_users_24h} />
        <StatCard label={t('admin.overview.newTenants24h')} value={stats.new_tenants_24h} />
        <StatCard label={t('admin.overview.apiCalls24h')} value={stats.api_calls_24h} />
        <StatCard label={t('admin.overview.storage')} value={bytesToHuman(stats.storage_bytes_used)} sub={t('admin.overview.ofLimit', { limit: bytesToHuman(stats.storage_bytes_limit) })} />
        <StatCard label={t('admin.overview.products')} value={stats.active_products} sub={t('admin.overview.ofTotal', { count: stats.total_products })} />
        <StatCard label={t('admin.overview.subscriptions')} value={stats.active_subscriptions} sub={t('admin.overview.ofTotal', { count: stats.total_subscriptions })} />
        <StatCard label={t('admin.overview.federationPeers')} value={stats.federation_peers_active} />
        <StatCard label={t('admin.overview.federatedLinks')} value={stats.federated_resource_links} />
        <StatCard label={t('admin.overview.mrr')} value={minorToDisplay(stats.mrr)} />
        <StatCard label={t('admin.overview.churnRate')} value={`${(stats.churn_rate_30d * 100).toFixed(1)}%`} />
      </div>

      <Card>
        <CardContent className="flex items-center justify-between gap-3 p-4">
          <div>
            <p className="text-sm font-medium text-foreground">{t('admin.overview.auditTrailShortcut.title')}</p>
            <p className="text-xs text-muted-foreground">{t('admin.overview.auditTrailShortcut.description')}</p>
          </div>
          <Link to="/audit" className="shrink-0 text-sm font-medium text-primary hover:underline">
            {t('admin.overview.auditTrailShortcut.action')}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
});

export default OverviewTab;
